import { Show, TextField } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Input, Typography, Space, Table, Popover, Card, Form, Button, Row, Col } from "antd";
import { useCustom } from "@refinedev/core";
import { TOKEN_KEY } from "../../../authProvider";
import { format } from "date-fns";
import { EyeOutlined, FilterOutlined } from "@ant-design/icons";
import UserSearchSelect from "../../components/UserSearchSelect";
import { useNavigate } from "react-router-dom";
import { cdnUrl } from "../../../consts";

const { Title, Text } = Typography;

export function PlanShow() {
  const { query } = useShow({});
  const { data, isFetching } = query;
  const plan = data?.data?.data;
  const apiUrl = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();

  // Add search form functionality
  const [searchForm] = Form.useForm();

  const { data: subscribersData, isLoading } = useCustom({
    method: "get",
    queryOptions: {
      enabled: !!plan?.id
    },
    url: `${apiUrl}/business_profile/product/${plan?.id}/subscription`,
    config: {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem(TOKEN_KEY)}`
      },
      query: {
        productID: plan?.id,
        q: searchForm.getFieldValue("q"),
        coachName: searchForm.getFieldValue("coachName"),
        subscriberID: searchForm.getFieldValue("subscriberID"),
      }
    },
  });

  const subscribers = subscribersData?.data?.list || [];

  const subscriberColumns = [
    {
      title: 'Subscriber email',
      dataIndex: ['user', 'email'],
      render: (value: string, record: any) => (
        <TextField
          value={value}
          onClick={() => {
            navigate(`/users/${record.user.id}`);
          }}
          style={{
              cursor: 'pointer',
              textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
          }}
        />
      )
    },
    {
      title: 'Subscriber name',
      dataIndex: ['user', 'fullName'],
      render: (value: string, record: any) => (
        <TextField
          value={value}
          onClick={() => {
            navigate(`/users/${record.user.id}`);
          }}
          style={{
              cursor: 'pointer',
              textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
          }}
        />
      )
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      render: (value: any) => (
        <Popover content={format(value, "MMMM d, yyyy 'at' h:mm a")}>
          <span>{format(value, "MMMM d, yyyy")}</span>
        </Popover>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (value: string) => (
        <Space style={{
          color: value === "ACTIVE" ? "green" : "red"
        }}>
          <TextField value={value} />
        </Space>
      ),
    },
    {
      title: 'Start date',
      dataIndex: 'startDate',
      render: (value: any) => (
        <Popover content={format(value, "MMMM d, yyyy 'at' h:mm a")}>
          <span>{format(value, "MMMM d, yyyy")}</span>
        </Popover>
      )
    },
    {
      title: 'End date',
      dataIndex: 'endDate',
      render: (value: any) => (
        <Popover content={format(value, "MMMM d, yyyy 'at' h:mm a")}>
          <span>{format(value, "MMMM d, yyyy")}</span>
        </Popover>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_: any, record: any) => (
        <EyeOutlined
          onClick={() => navigate(`/subscriptions/show/${record.id}`)}
          style={{ cursor: 'pointer' }}
        />
      )
    }
  ];

  const imageUrl = plan?.imageID ? `${cdnUrl}/${plan.imageID}` : null;

  return (
    <Show isLoading={isFetching}>
      <div style={{ padding: "24px 0", display: "flex", flexDirection: "column", gap: 24 }}>
        {/* Plan overview card */}
        <Card>
          <Row gutter={[24, 16]} align="middle">
            {imageUrl && (
              <Col xs={24} sm={24} md={8} lg={6}>
                <div style={{ borderRadius: 8, overflow: "hidden", maxWidth: 280, background: "#fafafa" }}>
                  <img
                    src={imageUrl}
                    alt={plan?.name}
                    style={{ width: "100%", height: "auto", display: "block", objectFit: "cover", maxHeight: 200 }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              </Col>
            )}
            <Col xs={24} sm={24} md={imageUrl ? 16 : 24} lg={imageUrl ? 18 : 24}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>Title</Text>
                  <Title level={4} style={{ margin: 0 }}>{plan?.name ?? "—"}</Title>
                </div>
                <Row gutter={24}>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Price</Text>
                    <div><Text strong>${Number(plan?.monthlyPrice ?? 0).toFixed(2)}</Text></div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                    <div><Text strong>{plan?.status ?? "—"}</Text></div>
                  </Col>
                  {plan?.productType && (
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Product type</Text>
                      <div><Text strong>{plan.productType}</Text></div>
                    </Col>
                  )}
                </Row>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Description card - label above content to avoid skewed layout */}
        <Card title="Description">
          {plan?.description ? (
            <Text style={{ whiteSpace: "pre-wrap", display: "block" }}>{plan.description}</Text>
          ) : (
            <Text type="secondary">No description.</Text>
          )}
        </Card>

        {/* Subscriptions section */}
        <Card title="Subscriptions">
          <Row gutter={24} wrap={false}>
            <Col flex="0 0 280px">
              <Form
                form={searchForm}
                layout="vertical"
                onFinish={() => query.refetch()}
              >
                <Form.Item name="q" label="Search">
                  <Input placeholder="Search by email or name" allowClear />
                </Form.Item>
                <Form.Item name="subscriberID" label="Subscriber">
                  <UserSearchSelect
                    userType={["USER"]}
                    onChange={(value) => searchForm.setFieldValue("subscriberID", value)}
                  />
                </Form.Item>
                <Form.Item name="coachName" label="Coach name">
                  <Input placeholder="Search by coach name" allowClear />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" icon={<FilterOutlined />} htmlType="submit" block>
                    Filter subscriptions
                  </Button>
                </Form.Item>
              </Form>
            </Col>
            <Col flex="1" style={{ minWidth: 0 }}>
              <Table
                dataSource={subscribers}
                columns={subscriberColumns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                  showTotal: (total) => `Total ${total} items`,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  onChange: (page, pageSize) => {
                    const start = (page - 1) * pageSize;
                    query.refetch({ ...searchForm.getFieldsValue(), _start: start, _end: start + pageSize });
                  },
                }}
              />
            </Col>
          </Row>
        </Card>
      </div>
    </Show>
  );
}